import { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, CheckCircle, XCircle, AlertTriangle, HelpCircle,
  ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight,
  MessageSquare, Eye, Check, X, Building2, User, CreditCard,
  Briefcase, Tag, Clock, ThumbsUp, ThumbsDown, SkipForward,
} from 'lucide-react';
import {
  useImportTransaktionen,
  ImportTransaktion,
  KATEGORIE_LABELS,
  TransaktionKategorie,
} from '../hooks/useImportTransaktionen';

// ============================================================
// Klärungs-Modal Komponente
// ============================================================
function KlaerungModal({
  transaktion,
  aehnliche,
  onKlaeren,
  onIgnorieren,
  onClose,
}: {
  transaktion: ImportTransaktion;
  aehnliche: ImportTransaktion[];
  onKlaeren: (kategorie: TransaktionKategorie, kommentar: string, istGeschaeftlich: boolean) => void;
  onIgnorieren: (kommentar: string) => void;
  onClose: () => void;
}) {
  const [kategorie, setKategorie] = useState<TransaktionKategorie>('sonstiges');
  const [kommentar, setKommentar] = useState('');
  const [istGeschaeftlich, setIstGeschaeftlich] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const betrag = transaktion.betrag;
  const istEinnahme = betrag > 0;

  // Intelligente Kategorie-Vorschläge basierend auf Beschreibung
  const vorschlaege: TransaktionKategorie[] = [];
  const beschr = (transaktion.beschreibung || '').toLowerCase();
  
  if (beschr.includes('tankstelle') || beschr.includes('bft') || beschr.includes('star ')) {
    vorschlaege.push('tankstelle', 'fahrzeug', 'privat');
  }
  if (beschr.includes('amazon') || beschr.includes('ebay') || beschr.includes('klarna')) {
    vorschlaege.push('buero_material', 'privat', 'sonstiges');
  }
  if (beschr.includes('provision') || beschr.includes('vermittlung')) {
    vorschlaege.push('provision', 'werbung_marketing');
  }
  if (beschr.includes('stripe')) {
    vorschlaege.push('sonstiges', 'software');
  }
  if (beschr.includes('rechnung') || beschr.includes('rg.nr')) {
    vorschlaege.push('zulieferer', 'buero_material');
  }
  if (beschr.includes('miete') || beschr.includes('pacht')) {
    vorschlaege.push('miete');
  }
  if (beschr.includes('gehalt') || beschr.includes('lohn')) {
    vorschlaege.push('gehalt');
  }
  
  // Fallback-Vorschläge
  if (vorschlaege.length === 0) {
    if (istEinnahme) {
      vorschlaege.push('versicherung', 'gutachten_zahlung', 'anwalt_regulierung', 'sonstiges');
    } else {
      vorschlaege.push('zulieferer', 'buero_material', 'fahrzeug', 'privat', 'sonstiges');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`p-6 rounded-t-2xl ${istEinnahme ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {istEinnahme ? (
                <ArrowUpRight className="w-5 h-5 text-emerald-600" />
              ) : (
                <ArrowDownRight className="w-5 h-5 text-red-600" />
              )}
              <span className="text-sm font-medium text-gray-500">
                {transaktion.firma === 'lcs' ? 'LCS GmbH' : 'SV Büro Koop'}
              </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className={`text-3xl font-bold ${istEinnahme ? 'text-emerald-700' : 'text-red-700'}`}>
            {istEinnahme ? '+' : ''}{betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} {transaktion.waehrung}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            {new Date(transaktion.buchung_datum).toLocaleDateString('de-DE', {
              weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            })}
          </div>
        </div>

        {/* Beschreibung */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Buchungstext</h3>
          <p className="text-gray-800 text-sm leading-relaxed">
            {transaktion.beschreibung}
          </p>
          {transaktion.klaerung_grund && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
              <HelpCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">{transaktion.klaerung_grund}</p>
            </div>
          )}
          
          {/* Rohtext Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="mt-2 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
          >
            <Eye className="w-3 h-3" />
            {showDetails ? 'Details ausblenden' : 'Vollständigen Buchungstext anzeigen'}
          </button>
          {showDetails && (
            <pre className="mt-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
              {transaktion.rohtext}
            </pre>
          )}
        </div>

        {/* Schnell-Zuordnung: Geschäftlich / Privat */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Zuordnung</h3>
          <div className="flex gap-3">
            <button
              onClick={() => setIstGeschaeftlich(true)}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                istGeschaeftlich
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Geschäftlich</span>
            </button>
            <button
              onClick={() => {
                setIstGeschaeftlich(false);
                setKategorie('privat');
              }}
              className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                !istGeschaeftlich
                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="font-medium">Privat</span>
            </button>
          </div>
        </div>

        {/* Kategorie-Auswahl */}
        {istGeschaeftlich && (
          <div className="p-6 border-b">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Kategorie wählen
            </h3>
            
            {/* Vorschläge */}
            {vorschlaege.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2">Vorschläge basierend auf dem Buchungstext:</p>
                <div className="flex flex-wrap gap-2">
                  {vorschlaege.map(v => (
                    <button
                      key={v}
                      onClick={() => setKategorie(v)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        kategorie === v
                          ? 'bg-teal-500 text-white'
                          : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                      }`}
                    >
                      {KATEGORIE_LABELS[v]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Alle Kategorien */}
            <details className="group">
              <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 mb-2">
                Alle Kategorien anzeigen
              </summary>
              <div className="flex flex-wrap gap-2 mt-2">
                {(Object.entries(KATEGORIE_LABELS) as [TransaktionKategorie, string][])
                  .filter(([k]) => k !== 'privat')
                  .map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setKategorie(k)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        kategorie === k
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
              </div>
            </details>
          </div>
        )}

        {/* Kommentar */}
        <div className="p-6 border-b">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Kommentar <span className="text-gray-400 font-normal">(optional)</span>
          </h3>
          <textarea
            value={kommentar}
            onChange={e => setKommentar(e.target.value)}
            placeholder="z.B. 'Kraftstoff für Firmenwagen' oder 'Privater Einkauf, nicht geschäftlich'"
            className="w-full p-3 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            rows={2}
          />
        </div>

        {/* Ähnliche Transaktionen */}
        {aehnliche.length > 1 && (
          <div className="p-6 border-b bg-blue-50/50">
            <h3 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              {aehnliche.length - 1} ähnliche Transaktionen gefunden
            </h3>
            <p className="text-xs text-blue-500 mb-3">
              Wenn Sie diese Transaktion klären, können Sie die gleiche Zuordnung auch für alle ähnlichen übernehmen.
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {aehnliche
                .filter(a => a.id !== transaktion.id)
                .slice(0, 5)
                .map(a => (
                  <div key={a.id} className="flex items-center justify-between text-xs text-blue-700 bg-white/60 p-2 rounded-lg">
                    <span>{new Date(a.buchung_datum).toLocaleDateString('de-DE')}</span>
                    <span className={a.betrag > 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {a.betrag > 0 ? '+' : ''}{a.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Aktionen */}
        <div className="p-6 flex gap-3">
          <button
            onClick={() => onKlaeren(kategorie, kommentar, istGeschaeftlich)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors"
          >
            <CheckCircle className="w-5 h-5" />
            Zuordnen & Speichern
          </button>
          <button
            onClick={() => onIgnorieren(kommentar || 'Nicht relevant')}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            <SkipForward className="w-5 h-5" />
            Überspringen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Hauptseite: Transaktions-Klärung
// ============================================================
export default function TransaktionKlaerungPage() {
  const {
    transaktionen,
    loading,
    stats,
    loadTransaktionen,
    loadStats,
    klaerenTransaktion,
    ignorierenTransaktion,
    bulkKlaeren,
    findeSimilar,
  } = useImportTransaktionen();

  const [filter, setFilter] = useState<{
    status: string;
    firma: string;
    suche: string;
  }>({ status: 'unklar', firma: '', suche: '' });

  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTx, setSelectedTx] = useState<ImportTransaktion | null>(null);
  const [aehnliche, setAehnliche] = useState<ImportTransaktion[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const PAGE_SIZE = 50;

  // Daten laden
  useEffect(() => {
    const load = async () => {
      const result = await loadTransaktionen(
        {
          status: filter.status || undefined,
          firma: filter.firma || undefined,
          suche: filter.suche || undefined,
        },
        page,
        PAGE_SIZE,
      );
      setTotalCount(result.count);
    };
    load();
  }, [filter, page, loadTransaktionen]);

  // Transaktion öffnen
  const openKlaerung = async (tx: ImportTransaktion) => {
    setSelectedTx(tx);
    if (tx.beschreibung) {
      const similar = await findeSimilar(tx.beschreibung);
      setAehnliche(similar);
    }
  };

  // Klärung speichern
  const handleKlaeren = async (
    kategorie: TransaktionKategorie,
    kommentar: string,
    istGeschaeftlich: boolean,
  ) => {
    if (!selectedTx) return;

    const success = await klaerenTransaktion(selectedTx.id, {
      kategorie,
      kommentar,
      ist_geschaeftlich: istGeschaeftlich,
    });

    if (success) {
      setSelectedTx(null);
      await loadStats();
      // Nächste unklare Transaktion automatisch öffnen
      const nextUnklar = transaktionen.find(
        t => t.id !== selectedTx.id && t.status === 'unklar'
      );
      if (nextUnklar) {
        setTimeout(() => openKlaerung(nextUnklar), 300);
      }
    }
  };

  // Ignorieren
  const handleIgnorieren = async (kommentar: string) => {
    if (!selectedTx) return;
    const success = await ignorierenTransaktion(selectedTx.id, kommentar);
    if (success) {
      setSelectedTx(null);
      await loadStats();
    }
  };

  // Fortschrittsbalken
  const fortschritt = stats.gesamt > 0
    ? ((stats.sicher + stats.geklaert + stats.ignoriert) / stats.gesamt) * 100
    : 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b bg-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Kontoauszug-Klärung</h1>
            <p className="text-sm text-gray-500 mt-1">
              Ordnen Sie unklare Transaktionen zu — per Klick kategorisieren und kommentieren
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-teal-600">{Math.round(fortschritt)}%</div>
            <div className="text-xs text-gray-400">geklärt</div>
          </div>
        </div>

        {/* Fortschrittsbalken */}
        <div className="w-full bg-gray-100 rounded-full h-3 mb-4">
          <div className="flex h-3 rounded-full overflow-hidden">
            <div
              className="bg-teal-500 transition-all duration-500"
              style={{ width: `${(stats.sicher / Math.max(stats.gesamt, 1)) * 100}%` }}
              title={`${stats.sicher} automatisch erkannt`}
            />
            <div
              className="bg-emerald-400 transition-all duration-500"
              style={{ width: `${(stats.geklaert / Math.max(stats.gesamt, 1)) * 100}%` }}
              title={`${stats.geklaert} manuell geklärt`}
            />
            <div
              className="bg-gray-300 transition-all duration-500"
              style={{ width: `${(stats.ignoriert / Math.max(stats.gesamt, 1)) * 100}%` }}
              title={`${stats.ignoriert} ignoriert`}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button
            onClick={() => setFilter(f => ({ ...f, status: '' }))}
            className={`p-3 rounded-xl text-center transition-all ${
              filter.status === '' ? 'bg-gray-800 text-white' : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="text-xl font-bold">{stats.gesamt.toLocaleString()}</div>
            <div className="text-xs opacity-70">Gesamt</div>
          </button>
          <button
            onClick={() => setFilter(f => ({ ...f, status: 'sicher' }))}
            className={`p-3 rounded-xl text-center transition-all ${
              filter.status === 'sicher' ? 'bg-teal-600 text-white' : 'bg-teal-50 hover:bg-teal-100 text-teal-700'
            }`}
          >
            <div className="text-xl font-bold">{stats.sicher.toLocaleString()}</div>
            <div className="text-xs opacity-70">Automatisch</div>
          </button>
          <button
            onClick={() => setFilter(f => ({ ...f, status: 'unklar' }))}
            className={`p-3 rounded-xl text-center transition-all ${
              filter.status === 'unklar' ? 'bg-amber-500 text-white' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'
            }`}
          >
            <div className="text-xl font-bold">{stats.unklar.toLocaleString()}</div>
            <div className="text-xs opacity-70">Offen</div>
          </button>
          <button
            onClick={() => setFilter(f => ({ ...f, status: 'geklaert' }))}
            className={`p-3 rounded-xl text-center transition-all ${
              filter.status === 'geklaert' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'
            }`}
          >
            <div className="text-xl font-bold">{stats.geklaert.toLocaleString()}</div>
            <div className="text-xs opacity-70">Geklärt</div>
          </button>
          <button
            onClick={() => setFilter(f => ({ ...f, status: 'ignoriert' }))}
            className={`p-3 rounded-xl text-center transition-all ${
              filter.status === 'ignoriert' ? 'bg-gray-600 text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
            }`}
          >
            <div className="text-xl font-bold">{stats.ignoriert.toLocaleString()}</div>
            <div className="text-xs opacity-70">Ignoriert</div>
          </button>
        </div>
      </div>

      {/* Filter-Leiste */}
      <div className="px-6 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Transaktionen durchsuchen..."
            value={filter.suche}
            onChange={e => { setFilter(f => ({ ...f, suche: e.target.value })); setPage(0); }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <select
          value={filter.firma}
          onChange={e => { setFilter(f => ({ ...f, firma: e.target.value })); setPage(0); }}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">Alle Firmen</option>
          <option value="lcs">LCS GmbH</option>
          <option value="sv_buero_koop">SV Büro Koop</option>
        </select>
        <div className="text-sm text-gray-500">
          {totalCount.toLocaleString()} Transaktionen
        </div>
      </div>

      {/* Transaktionsliste */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
          </div>
        ) : transaktionen.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <CheckCircle className="w-12 h-12 mb-3" />
            <p className="text-lg font-medium">Keine Transaktionen in dieser Ansicht</p>
          </div>
        ) : (
          <div className="divide-y">
            {transaktionen.map(tx => (
              <button
                key={tx.id}
                onClick={() => openKlaerung(tx)}
                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
              >
                {/* Status-Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  tx.status === 'sicher' ? 'bg-teal-100' :
                  tx.status === 'geklaert' ? 'bg-emerald-100' :
                  tx.status === 'ignoriert' ? 'bg-gray-100' :
                  'bg-amber-100'
                }`}>
                  {tx.status === 'sicher' ? <CheckCircle className="w-5 h-5 text-teal-600" /> :
                   tx.status === 'geklaert' ? <Check className="w-5 h-5 text-emerald-600" /> :
                   tx.status === 'ignoriert' ? <SkipForward className="w-5 h-5 text-gray-500" /> :
                   <AlertTriangle className="w-5 h-5 text-amber-600" />}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {tx.beschreibung?.split(/\s+/).slice(0, 4).join(' ') || 'Unbekannt'}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      tx.firma === 'lcs' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {tx.firma === 'lcs' ? 'LCS' : 'SV'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {new Date(tx.buchung_datum).toLocaleDateString('de-DE')}
                    {tx.kategorie && (
                      <span className="ml-2 text-gray-500">
                        · {KATEGORIE_LABELS[tx.kategorie as TransaktionKategorie] || tx.kategorie}
                      </span>
                    )}
                    {tx.klaerung_kommentar && (
                      <span className="ml-2 text-teal-500 flex items-center gap-1 inline-flex">
                        <MessageSquare className="w-3 h-3" />
                        {tx.klaerung_kommentar.substring(0, 30)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Betrag */}
                <div className={`text-right flex-shrink-0 ${
                  tx.betrag > 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  <div className="text-sm font-semibold">
                    {tx.betrag > 0 ? '+' : ''}{tx.betrag.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-xs text-gray-400">{tx.waehrung}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <div className="px-6 py-3 border-t bg-white flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4" /> Zurück
          </button>
          <span className="text-sm text-gray-500">
            Seite {page + 1} von {Math.ceil(totalCount / PAGE_SIZE)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= totalCount}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
          >
            Weiter <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Klärungs-Modal */}
      {selectedTx && (
        <KlaerungModal
          transaktion={selectedTx}
          aehnliche={aehnliche}
          onKlaeren={handleKlaeren}
          onIgnorieren={handleIgnorieren}
          onClose={() => setSelectedTx(null)}
        />
      )}
    </div>
  );
}
